import type { DailyEntryResponse } from "@consistency-circle/shared";

import { addDays } from "./goal-stats";

export type WeeklyInsight = {
  readonly kind: "rough-week" | "correlation";
  readonly text: string;
  /** Gap between the two group means, in mood/rating points (1-5 scale). */
  readonly strength: number;
  readonly sampleSize: number;
};

type RatingFactor = {
  readonly key: "sleepQuality" | "dietQuality" | "exerciseQuality";
  readonly label: string;
};

const ratingFactors: RatingFactor[] = [
  { key: "sleepQuality", label: "sleep quality" },
  { key: "dietQuality", label: "diet" },
  { key: "exerciseQuality", label: "exercise" },
];

const LOW_MOOD = 2;
const ROUGH_DAYS_THRESHOLD = 3;
const ROUGH_WINDOW_DAYS = 7;
/** Minimum logged days per group before a rough-week factor can be named. */
const MIN_FACTOR_DAYS = 2;
/** Minimum gap (rating points) before a rough-week factor is called out. */
const MIN_FACTOR_GAP = 0.5;

const MIN_GROUP_SIZE = 3;
const MIN_DIFFERENCE = 0.7;
const SHORT_SLEEP_HOURS = 6.5;

const mean = (values: number[]): number =>
  values.reduce((sum, value) => sum + value, 0) / values.length;

const formatRating = (value: number): string => value.toFixed(1);

/** Map sleep hours onto the same 1-5 scale as the ratings: 4h → 1, 8h+ → 5. */
const sleepHoursAsRating = (hours: number): number =>
  Math.min(5, Math.max(1, 1 + (hours - 4)));

/**
 * The "rough week" check: when several of the last few days were low-mood,
 * name the factor (sleep, diet, exercise) that most separates the rough days
 * from the better ones.
 */
function pickRoughWeekInsight(entries: DailyEntryResponse[]): WeeklyInsight | null {
  const moodDays = entries.filter((entry) => entry.mood !== null);
  if (moodDays.length === 0) return null;

  const latestDate = moodDays.reduce(
    (latest, entry) => (entry.localDate > latest ? entry.localDate : latest),
    moodDays[0]!.localDate,
  );
  const windowStart = addDays(latestDate, -(ROUGH_WINDOW_DAYS - 1));
  const window = moodDays.filter((entry) => entry.localDate >= windowStart);

  const lowDays = window.filter((entry) => (entry.mood ?? 0) <= LOW_MOOD);
  const betterDays = window.filter((entry) => (entry.mood ?? 0) > LOW_MOOD);
  if (lowDays.length < ROUGH_DAYS_THRESHOLD || betterDays.length < MIN_FACTOR_DAYS) {
    return null;
  }

  type Culprit = { label: string; lowMean: number; betterMean: number; gap: number };
  const culprits: Culprit[] = [];

  const consider = (label: string, lowValues: number[], betterValues: number[]) => {
    if (lowValues.length < MIN_FACTOR_DAYS || betterValues.length < MIN_FACTOR_DAYS) return;
    const lowMean = mean(lowValues);
    const betterMean = mean(betterValues);
    const gap = betterMean - lowMean;
    if (gap < MIN_FACTOR_GAP) return;
    culprits.push({ label, lowMean, betterMean, gap });
  };

  for (const factor of ratingFactors) {
    consider(
      factor.label,
      lowDays.map((entry) => entry[factor.key]).filter((v): v is number => v !== null),
      betterDays.map((entry) => entry[factor.key]).filter((v): v is number => v !== null),
    );
  }
  consider(
    "sleep",
    lowDays
      .map((entry) => entry.sleepHours)
      .filter((v): v is number => v !== null)
      .map(sleepHoursAsRating),
    betterDays
      .map((entry) => entry.sleepHours)
      .filter((v): v is number => v !== null)
      .map(sleepHoursAsRating),
  );

  const prefix = `${lowDays.length} of your last ${window.length} check-ins were low.`;
  const found = culprits.reduce<Culprit | null>(
    (best, candidate) => (!best || candidate.gap > best.gap ? candidate : best),
    null,
  );

  if (!found) {
    return {
      kind: "rough-week",
      text: `${prefix} Nothing in your sleep, diet, or exercise logs clearly sets those days apart yet — keep logging them daily to find out.`,
      strength: 0,
      sampleSize: window.length,
    };
  }

  return {
    kind: "rough-week",
    text: `${prefix} On those days your ${found.label} averaged ${formatRating(found.lowMean)} vs ${formatRating(found.betterMean)} on your better days.`,
    strength: found.gap,
    sampleSize: window.length,
  };
}

function compareGroups(
  goodDays: number[],
  poorDays: number[],
  goodLabel: string,
  poorLabel: string,
): WeeklyInsight | null {
  if (goodDays.length < MIN_GROUP_SIZE || poorDays.length < MIN_GROUP_SIZE) {
    return null;
  }

  const meanGood = mean(goodDays);
  const meanPoor = mean(poorDays);
  const difference = Math.abs(meanGood - meanPoor);
  if (difference < MIN_DIFFERENCE) return null;

  return {
    kind: "correlation",
    text: `On days ${goodLabel}, your mood averaged ${formatRating(meanGood)} vs ${formatRating(meanPoor)} on days ${poorLabel}.`,
    strength: difference,
    sampleSize: goodDays.length + poorDays.length,
  };
}

/**
 * Pick the most useful pattern in recent entries. A rough week (3+ low-mood
 * days in the last 7) takes priority and names the factor — sleep, diet, or
 * exercise — that most separates the rough days from the better ones.
 * Otherwise fall back to the strongest factor-vs-mood correlation across the
 * whole window. Correlation only, so groups need at least MIN_GROUP_SIZE days
 * each and a clear gap to qualify.
 */
export function pickWeeklyInsight(entries: DailyEntryResponse[]): WeeklyInsight | null {
  const roughWeek = pickRoughWeekInsight(entries);
  if (roughWeek) return roughWeek;

  const candidates: WeeklyInsight[] = [];

  for (const factor of ratingFactors) {
    const goodDays: number[] = [];
    const poorDays: number[] = [];

    for (const entry of entries) {
      if (entry.mood === null) continue;
      const rating = entry[factor.key];
      if (rating === null) continue;
      if (rating >= 4) goodDays.push(entry.mood);
      else if (rating <= 2) poorDays.push(entry.mood);
    }

    const insight = compareGroups(
      goodDays,
      poorDays,
      `you rated your ${factor.label} 4+`,
      `you rated it 2 or lower`,
    );
    if (insight) candidates.push(insight);
  }

  const restedDays: number[] = [];
  const shortSleepDays: number[] = [];
  for (const entry of entries) {
    if (entry.mood === null || entry.sleepHours === null) continue;
    if (entry.sleepHours < SHORT_SLEEP_HOURS) shortSleepDays.push(entry.mood);
    else restedDays.push(entry.mood);
  }
  const sleepInsight = compareGroups(
    restedDays,
    shortSleepDays,
    `you slept ${SHORT_SLEEP_HOURS}h or more`,
    `you slept less`,
  );
  if (sleepInsight) candidates.push(sleepInsight);

  if (candidates.length === 0) return null;
  return candidates.reduce((best, candidate) =>
    candidate.strength > best.strength ? candidate : best,
  );
}
